import React from 'react';
import { Dialog, type DialogProps } from '@allygpo/ally-ux';

interface ModalProps {
  visible: boolean;
  onHide: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Right-aligned footer actions. */
  footer?: React.ReactNode;
  /** Left side of the footer, e.g. "3 unsaved changes". */
  footerStart?: React.ReactNode;
  /** Max width in rem-based units, e.g. "64.5312rem" for the 1180px eligibility modal. Defaults to 1040px. */
  width?: string;
  /** Fixed height, for tabbed dialogs, so switching tabs doesn't resize and re-centre the dialog. */
  height?: string;
  /** Tabs or a toolbar between the header and the scrolling body. */
  beforeBody?: React.ReactNode;
  className?: string;
  /** Called after the close animation finishes. */
  onExited?: () => void;
  /** Close when the scrim is clicked. Off by default so edits aren't lost by a stray click. */
  dismissableMask?: boolean;
  dialogProps?: Partial<DialogProps>;
}

/**
 * Standard modal: Ally UX Dialog with its own title, close, padding and footer bar. Adds a muted subtitle,
 * a note on the footer's left, and an optional row (tabs) that stays above the scrolling body. Esc closes it.
 */
/** Dialog title with a muted subtitle under it; the title keeps Dialog's own type. */
export const ModalHeading: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode }> = ({ title, subtitle }) => (
  <span className="flex flex-column u-gap-1 min-w-0">
    <span>{title}</span>
    {subtitle && <span className="text-xs font-normal text-secondary">{subtitle}</span>}
  </span>
);

const Modal: React.FC<ModalProps> = ({ visible, onHide, title, subtitle, children, footer, footerStart, width = '56.875rem', height, beforeBody, className, onExited, dismissableMask = false, dialogProps }) => (
  <Dialog
    visible={visible}
    onHide={onHide}
    header={<ModalHeading title={title} subtitle={subtitle} />}
    footer={(footer || footerStart) ? (
      <div className="flex align-items-center justify-content-between u-gap-3 w-full">
        <span className="text-xs text-secondary min-w-0">{footerStart}</span>
        <div className="flex u-gap-2 flex-wrap justify-content-end">{footer}</div>
      </div>
    ) : undefined}
    className={`siq-modal${beforeBody ? ' has-before-body' : ''}${className ? ` ${className}` : ''}`}
    style={{ width, maxWidth: 'calc(100vw - 2.625rem)', height }}
    modal
    closeOnEscape
    dismissableMask={dismissableMask}
    draggable={false}
    resizable={false}
    blockScroll={false}
    focusOnShow={false}
    transitionOptions={onExited ? { timeout: { enter: 240, exit: 150 }, onExited } as any : { timeout: { enter: 240, exit: 150 } } as any}
    {...dialogProps}
  >
    {beforeBody && <div className="siq-modal-before-body">{beforeBody}</div>}
    {children}
  </Dialog>
);

export default Modal;
